import React, { useState, useEffect } from 'react';
import { getFlocks, deleteFlock, fileFlock } from '../../../services/butcherLogService';
import FlockForm from './FlockForm';
import FlockDetail from './FlockDetail';
import './ButcherLog.css';

export default function ButcherLog() {
  const [view, setView] = useState('list'); // 'list' | 'new' | 'edit' | 'detail'
  const [flocks, setFlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFlock, setSelectedFlock] = useState(null);
  const [activeArchiveTab, setActiveArchiveTab] = useState('active');

  useEffect(() => {
    if (view === 'list') loadFlocks();
  }, [view]);

  async function loadFlocks() {
    setLoading(true);
    const res = await getFlocks();
    if (res.success) {
      const sorted = res.data.sort((a, b) => new Date(b.arrivalDate) - new Date(a.arrivalDate));
      setFlocks(sorted);
    }
    setLoading(false);
  }

  async function handleFile(flock) {
    if (!flock.butcherDate) {
      alert('Cannot file flock — butcher date is required.');
      return;
    }
    if (!window.confirm(`File flock ${flock.flockId}? It will move to the archive.`)) return;
    const res = await fileFlock(flock.flockId);
    if (res.success) loadFlocks();
    else alert('Error: ' + res.error);
  }

  async function handleDelete(flock) {
    if (!window.confirm(`Delete flock ${flock.flockId}?`)) return;
    const res = await deleteFlock(flock.flockId);
    if (res.success) loadFlocks();
    else alert('Error: ' + res.error);
  }

  const activeFlocks = flocks.filter(f => f.filed !== 'yes');
  const filedFlocks = flocks.filter(f => f.filed === 'yes');

  if (view === 'new' || view === 'edit') {
    return (
      <FlockForm
        flock={selectedFlock}
        onSaved={() => { setSelectedFlock(null); setView('list'); }}
        onCancel={() => { setSelectedFlock(null); setView('list'); }}
      />
    );
  }

  if (view === 'detail') {
    return (
      <FlockDetail
        flock={selectedFlock}
        onBack={() => { setSelectedFlock(null); setView('list'); }}
        onEdit={() => setView('edit')}
        onFile={() => { handleFile(selectedFlock); setView('list'); }}
        onRefresh={loadFlocks}
      />
    );
  }

  return (
    <div className="bl-container">
      <div className="bl-header">
        <h1>🐔 Broiler Chicken Log</h1>
        <button className="bl-btn-primary" onClick={() => { setSelectedFlock(null); setView('new'); }}>
          + New Flock
        </button>
      </div>

      {/* Active Flocks */}
      <div className="bl-section">
        <h2 className="bl-section-title">🟢 Active Flocks</h2>
        {loading ? (
          <div className="bl-loading">Loading...</div>
        ) : activeFlocks.length === 0 ? (
          <div className="bl-empty">No active flocks. Start by adding a new flock.</div>
        ) : (
          <div className="bl-flock-list">
            {activeFlocks.map(flock => (
              <FlockCard
                key={flock.flockId}
                flock={flock}
                onOpen={() => { setSelectedFlock(flock); setView('detail'); }}
                onEdit={() => { setSelectedFlock(flock); setView('edit'); }}
                onFile={() => handleFile(flock)}
                onDelete={() => handleDelete(flock)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Archived Flocks */}
      <div className="bl-section">
        <h2 className="bl-section-title">📁 Flock Archive</h2>
        {filedFlocks.length === 0 ? (
          <div className="bl-empty">No archived flocks yet.</div>
        ) : (
          <div className="bl-flock-list">
            {filedFlocks.map(flock => (
              <FlockCard
                key={flock.flockId}
                flock={flock}
                archived
                onOpen={() => { setSelectedFlock(flock); setView('detail'); }}
                onEdit={() => { setSelectedFlock(flock); setView('edit'); }}
                onDelete={() => handleDelete(flock)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FlockCard({ flock, archived, onOpen, onEdit, onFile, onDelete }) {
  let mortality = 0;
  try {
    const log = JSON.parse(flock.mortalityLog || '[]');
    mortality = log.reduce((sum, e) => sum + (parseInt(e.count) || 0), 0);
  } catch {}

  const stage = !flock.butcherDate
    ? (!flock.brooderToPastureDate ? '🐣 Brooder' : '🌿 Pasture')
    : '✅ Butchered';

  return (
    <div className="bl-flock-card">
      <div className="bl-flock-card-body">
        <div className="bl-flock-top">
          <span className="bl-flock-id">{flock.flockId}</span>
          <span className="bl-stage-badge">{stage}</span>
          {archived && <span className="bl-filed-badge">Filed</span>}
        </div>
        <div className="bl-flock-meta">
          <span>Arrived: {flock.arrivalDate ? String(flock.arrivalDate).slice(0,10) : '—'}</span>
          <span>Total: {flock.totalBirds} birds</span>
          <span>AR: {flock.arBirds} · {flock.growerName || 'No grower'}: {flock.growerBirds || 0}</span>
          {flock.butcherDate && <span>Butchered: {String(flock.butcherDate).slice(0,10)}</span>}
          {mortality > 0 && <span className="bl-mortality">⚠️ {mortality} lost</span>}
        </div>
        {flock.notes && <div className="bl-flock-notes">{flock.notes}</div>}
      </div>
      <div className="bl-flock-actions">
        <button className="bl-btn-open" onClick={onOpen}>Open</button>
        <button className="bl-btn-edit" onClick={onEdit}>Edit</button>
        {!archived && onFile && (
          <button className="bl-btn-file" onClick={onFile}>File</button>
        )}
        <button className="bl-btn-delete" onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
}
